/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { IFilterField } from "Interfaces/IFilterField";
import { IKeyText } from "Interfaces/IKeyText";

import { ApplicationStatusTypesEnum } from "./ApplicationStatusTypes";
import { fieldTypes, FilterValueFieldNames } from "./FilterFields";
import { RolesEnum, RoleNamesEnum } from "./RolesPermissionsConstants";

export const defaultListingPageSize = 16;

export enum ApplicationTypeEnum {
  ANY = "Any",
  NEW = "New",
  RENEW = "Renew",
}

export enum ApplicationTabs {
  ProfileInformation = "profile-information",
  ApplicationQuestions = "application-questions",
  HighlightedActivitiesEvents = "highlighted-activities-events",
  AdditionalActivitiesEvents = "additional-activities-events",
  Nomination = "nomination",
  ApplicationFlags = "application-flags",
}

/**
 * The Questions Section StringEnum.
 * @description Section name for application questions.
 * @enum
 */
export enum QuestionsFormSectionStringEnum {
  PROFILE_INFORMATION = "Profile Information",
}

/**
 * field used to sort the profile-section questions
 */
export const OrderBy = "orderBy";

export const applicationTypes: IKeyText[] = [
  {
    key: ApplicationTypeEnum.ANY,
    text: ApplicationTypeEnum.ANY,
  },
  {
    key: ApplicationTypeEnum.NEW,
    text: ApplicationTypeEnum.NEW,
  },
  {
    key: ApplicationTypeEnum.RENEW,
    text: ApplicationTypeEnum.RENEW,
  },
];

export const applicationTypesWithoutAny: IKeyText[] = [
  {
    key: ApplicationTypeEnum.NEW,
    text: ApplicationTypeEnum.NEW,
  },
  {
    key: ApplicationTypeEnum.RENEW,
    text: ApplicationTypeEnum.RENEW,
  },
];

/**
 * Values for application status
 */
export const applicationStatusMLSA: IKeyText[] = [
  {
    key: ApplicationStatusTypesEnum.INVITED,
    text: ApplicationStatusTypesEnum.INVITED,
  },
  {
    key: ApplicationStatusTypesEnum.SAVED,
    text: ApplicationStatusTypesEnum.SAVED,
  },
  {
    key: ApplicationStatusTypesEnum.UNDER_REVIEW,
    text: ApplicationStatusTypesEnum.UNDER_REVIEW,
  },
  {
    key: ApplicationStatusTypesEnum.Declined,
    text: ApplicationStatusTypesEnum.Declined,
  },
];
export const applicationStatusMvp: IKeyText[] = [
  {
    key: ApplicationStatusTypesEnum.INVITED,
    text: ApplicationStatusTypesEnum.INVITED,
  },
  {
    key: ApplicationStatusTypesEnum.SAVED,
    text: ApplicationStatusTypesEnum.SAVED,
  },
  {
    key: ApplicationStatusTypesEnum.Submitted,
    text: ApplicationStatusTypesEnum.Submitted,
  },
  {
    key: ApplicationStatusTypesEnum.REVIEW_ELIGIBLE,
    text: ApplicationStatusTypesEnum.REVIEW_ELIGIBLE,
  },
  {
    key: ApplicationStatusTypesEnum.UNDER_REVIEW,
    text: ApplicationStatusTypesEnum.UNDER_REVIEW,
  },
  {
    key: ApplicationStatusTypesEnum.Declined,
    text: ApplicationStatusTypesEnum.Declined,
  },
  {
    key: ApplicationStatusTypesEnum.NOT_SUBMITTED,
    text: ApplicationStatusTypesEnum.NOT_SUBMITTED,
  },
];
export const applicationStatusRd: IKeyText[] = [
  {
    key: ApplicationStatusTypesEnum.INVITED,
    text: ApplicationStatusTypesEnum.INVITED,
  },
  {
    key: ApplicationStatusTypesEnum.SAVED,
    text: ApplicationStatusTypesEnum.SAVED,
  },
  {
    key: ApplicationStatusTypesEnum.REVIEW_ELIGIBLE,
    text: ApplicationStatusTypesEnum.REVIEW_ELIGIBLE,
  },
  {
    key: ApplicationStatusTypesEnum.UNDER_REVIEW,
    text: ApplicationStatusTypesEnum.UNDER_REVIEW,
  },
  {
    key: ApplicationStatusTypesEnum.Declined,
    text: ApplicationStatusTypesEnum.Declined,
  },
];

export enum TechnologyAreaStatusEnum {
  DecisionPending = "Decision-Pending",
  InvitedPending = "Invited-Pending",
  DeclinedPending = "Declined-Pending",
}

export const technologyAreaStatuses = [
  {
    key: TechnologyAreaStatusEnum.DecisionPending,
    text: TechnologyAreaStatusEnum.DecisionPending,
  },
  {
    key: TechnologyAreaStatusEnum.InvitedPending,
    text: TechnologyAreaStatusEnum.InvitedPending,
  },
  {
    key: TechnologyAreaStatusEnum.DeclinedPending,
    text: TechnologyAreaStatusEnum.DeclinedPending,
  },
];

export const ANY = "Any";
export const ME = "Me";

/**
 * Any or me options
 */
export const anyOrMeOptions: IKeyText[] = [
  {
    key: ANY,
    text: ANY,
  },
  {
    key: ME,
    text: ME,
  },
];

export enum assignedOptions {
  ANY = "Any",
  ME = "Me",
  NOT_ASSIGNED = "Not Assigned",
}
export const anyOrMeOptionsList = [
  {
    key: assignedOptions.ANY,
    text: assignedOptions.ANY,
  },
  {
    key: assignedOptions.ME,
    text: assignedOptions.ME,
  },
  {
    key: assignedOptions.NOT_ASSIGNED,
    text: assignedOptions.NOT_ASSIGNED,
  },
];
export const flagRoleOptions: IKeyText[] = [
  {
    key: RolesEnum.MVP_REVIEWER,
    text: RoleNamesEnum.COMMUNITY_MANAGER,
  },
];

export enum technologyAreaStatusReasonEnum {
  BelowMinScore = "Below Min Score",
  MayWorkForCompetitor = "May work for a competitor",
  NotScored = "Not Scored",
  ManualDecline = "Manual Decline",
  RenewalNotSubmitted = "Renewal not submitted",
  ExceedsCap = "Exceeds Cap",
  NeedManualInput = "Need manual input",
}

export const technologyAreaStatusReasons = [
  {
    key: technologyAreaStatusReasonEnum.BelowMinScore,
    text: technologyAreaStatusReasonEnum.BelowMinScore,
  },
  {
    key: technologyAreaStatusReasonEnum.ExceedsCap,
    text: technologyAreaStatusReasonEnum.ExceedsCap,
  },
  {
    key: technologyAreaStatusReasonEnum.MayWorkForCompetitor,
    text: technologyAreaStatusReasonEnum.MayWorkForCompetitor,
  },
  {
    key: technologyAreaStatusReasonEnum.ManualDecline,
    text: technologyAreaStatusReasonEnum.ManualDecline,
  },
  {
    key: technologyAreaStatusReasonEnum.NotScored,
    text: technologyAreaStatusReasonEnum.NotScored,
  },
  {
    key: technologyAreaStatusReasonEnum.RenewalNotSubmitted,
    text: technologyAreaStatusReasonEnum.RenewalNotSubmitted,
  },
  {
    key: technologyAreaStatusReasonEnum.NeedManualInput,
    text: technologyAreaStatusReasonEnum.NeedManualInput,
  },
];

/**
 * The default error message that is thrown when a call to an external service times out
 */
export const GENERIC_API_TIMEOUT_ERROR =
  "A timeout error has occurred. Please try again later. If the issue persists, contact support.";

/**
 * Setting of constants for default field strings
 * we use to set in reducer.
 */
export const APPLICATION_STATUS = "applicationStatus";

export const APPLICATION_TYPE = "applicationType";

export const APPLICATION_COUNTRY_REGION = "country";

export const APPLICATION_STATE_PROVINCE = "applicationStateProvince";

export const GRANT_ANNUAL_EXCEPTION = "grantAnnualException";

export const APPLICATION_REGION = "region";

export const AWARD_CATEGORY = "awardCategory";

export const TECHNOLOGY_AREA = "technologyArea";

export const TECHNOLOGY_AREA_GROUP = "technologyAreaGroup";

export const ASSIGNED_TO = "assignedTo";

export const GENDER = "gender";

export const TECHNOLOGY_AREA_STATUS = "technologyAreaStatus";

export const TECHNOLOGY_AREA_STATUS_REASON = "technologyAreaStatusReason";

export const FLAG_REASON = "flagReason";

export const COHORT = "cohort";

export const TECHNOLOGY_AREA_SCORE = "technologyAreaScore";

export const REVIEW_STATUS = "reviewStatuses";

export const AVAILABLE_CPM_LIST = "availableCpmList";

export const GOVERNMENT_EMPLOYEE_MESSAGE = "Govt. Employee";

export const COMPETITOR_MESSAGE = "May work for a competitor";

/**
 * Sidebar filter constants used for rendering
 */
export const studentsApplicationFilterFields: IFilterField[] = [
  {
    key: APPLICATION_STATUS,
    text: "Application Status",
    type: fieldTypes.MULTI_SELECT,
    options: applicationStatusMLSA,
  },
  {
    key: APPLICATION_COUNTRY_REGION,
    text: "Country/Region",
    type: fieldTypes.COUNTRY,
  },
  {
    key: APPLICATION_STATE_PROVINCE,
    text: "State/Province",
    type: fieldTypes.PROVINCE,
  },
];

export enum ApplicationCohortEnum {
  Any = "Any",
  Current = "Current",
  Future = "Current + 1",
  Past = "Current - 1",
}

export const mvpAnnualExceptionOptions: IKeyText[] = [
  {
    key: "Yes",
    text: "Yes",
  },
  {
    key: "No",
    text: "No",
  },
];

export const cohortOptions = [
  {
    key: ApplicationCohortEnum.Any,
    text: ApplicationCohortEnum.Any,
  },
  {
    key: ApplicationCohortEnum.Current,
    text: ApplicationCohortEnum.Current,
  },
  {
    key: ApplicationCohortEnum.Future,
    text: ApplicationCohortEnum.Future,
  },
  {
    key: ApplicationCohortEnum.Past,
    text: ApplicationCohortEnum.Past,
  },
];

export enum FlagReasons {
  WrongCategoryTechArea = "Wrong Award Category / Technology area",
  Misconduct = "Misconduct",
  MVPRequestedToBeRetired = "MVP requested to be retired",
  NeedsAdditionalReview = "Needs additional review",
  GovernmentOfficial = "Government official",
  VendorAtMicrosoft = "Vendor at Microsoft",
  HiredByMicrosoft = "Hired by Microsoft",
  UnscoredApplication = "Unscored application",
  NotFlagged = "Not flagged",
}

export const REASON_FOR_FLAG_DROPDOWN_OPTIONS = [
  { key: "", text: "Any" },
  { key: "1", text: FlagReasons.WrongCategoryTechArea },
  { key: "2", text: FlagReasons.Misconduct },
  { key: "3", text: FlagReasons.MVPRequestedToBeRetired },
  { key: "4", text: FlagReasons.NeedsAdditionalReview },
  { key: "5", text: FlagReasons.GovernmentOfficial },
  { key: "6", text: FlagReasons.VendorAtMicrosoft },
  { key: "7", text: FlagReasons.HiredByMicrosoft },
  { key: "8", text: FlagReasons.UnscoredApplication },
];

export const REASON_FOR_FLAG_DROPDOWN_MULTISELECT_OPTIONS = [
  {
    key: FlagReasons.WrongCategoryTechArea,
    text: FlagReasons.WrongCategoryTechArea,
  },
  {
    key: FlagReasons.Misconduct,
    text: FlagReasons.Misconduct,
  },
  {
    key: FlagReasons.MVPRequestedToBeRetired,
    text: FlagReasons.MVPRequestedToBeRetired,
  },
  {
    key: FlagReasons.NeedsAdditionalReview,
    text: FlagReasons.NeedsAdditionalReview,
  },
  {
    key: FlagReasons.GovernmentOfficial,
    text: FlagReasons.GovernmentOfficial,
  },
  {
    key: FlagReasons.VendorAtMicrosoft,
    text: FlagReasons.VendorAtMicrosoft,
  },
  {
    key: FlagReasons.HiredByMicrosoft,
    text: FlagReasons.HiredByMicrosoft,
  },
  {
    key: FlagReasons.UnscoredApplication,
    text: FlagReasons.UnscoredApplication,
  },
  {
    key: FlagReasons.NotFlagged,
    text: FlagReasons.NotFlagged,
  },
];

/**
 * Sidebar filter constants used for rendering
 */
export const mvpApplicationFilterFields: IFilterField[] = [
  {
    key: APPLICATION_TYPE,
    text: "Application Type",
    type: fieldTypes.SELECT,
    options: applicationTypes,
  },
  {
    key: APPLICATION_STATUS,
    text: "Application Status",
    type: fieldTypes.MULTI_SELECT,
    options: applicationStatusMvp,
  },
  {
    key: FilterValueFieldNames.REVIEW_STATUS,
    text: "Review Status",
    type: fieldTypes.REVIEW_STATUS,
    options: anyOrMeOptions,
  },
  {
    key: FilterValueFieldNames.COHORT,
    text: "Cohort",
    type: fieldTypes.SELECT,
    options: cohortOptions,
  },
  {
    key: FilterValueFieldNames.ASSIGNED_TO,
    text: "Assigned To",
    type: fieldTypes.SELECT,
    options: anyOrMeOptionsList,
  },
  {
    key: FilterValueFieldNames.TECHNOLOGY_AREA_GROUP,
    text: "Award Category",
    type: fieldTypes.TECHNOLOGY_AREA_GROUP,
  },
  {
    key: FilterValueFieldNames.TECHNOLOGY_AREA,
    text: "Technology Area",
    type: fieldTypes.TECHNOLOGY_AREA,
  },
  {
    key: FilterValueFieldNames.TECHNOLOGY_AREA_STATUS,
    text: "TA Status",
    type: fieldTypes.MULTI_SELECT,
    options: technologyAreaStatuses,
  },
  {
    key: FilterValueFieldNames.SCORE,
    text: "TA Score",
    type: fieldTypes.MULTI_SELECT,
    options: [
      { key: "0", text: "Not Scored" },
      ...[1, 2, 3, 4, 5].map((x) => ({ key: x.toString(), text: x.toString() })),
    ],
  },
  {
    key: FilterValueFieldNames.TECHNOLOGY_AREA_STATUS_REASON,
    text: "TA Reason",
    type: fieldTypes.MULTI_SELECT,
    options: technologyAreaStatusReasons.sort((a, b) => a.text.localeCompare(b.text)),
  },
  {
    key: FilterValueFieldNames.FLAG_REASON,
    text: "Reason for Flag",
    type: fieldTypes.MULTI_SELECT,
    options: REASON_FOR_FLAG_DROPDOWN_MULTISELECT_OPTIONS,
  },
  {
    key: FilterValueFieldNames.AVAILABLE_CPM_LIST,
    text: "CPM List",
    type: fieldTypes.AVAILABLE_CPM_LIST,
  },
  {
    key: FilterValueFieldNames.COUNTRY,
    text: "Country",
    type: fieldTypes.COUNTRY,
  },
  {
    key: APPLICATION_STATE_PROVINCE,
    text: "State/Province",
    type: fieldTypes.PROVINCE,
  },
  {
    key: FilterValueFieldNames.GRANT_ANNUAL_EXCEPTION,
    text: "Grant Annual Exception",
    type: fieldTypes.MULTI_SELECT,
    options: mvpAnnualExceptionOptions,
  },
];

/**
 * Sidebar filter constants used for rendering
 */
export const rdApplicationFilterFields: IFilterField[] = [
  {
    key: APPLICATION_TYPE,
    text: "Application Type",
    type: fieldTypes.SELECT,
    options: applicationTypes,
  },
  {
    key: APPLICATION_STATUS,
    text: "Application Status",
    type: fieldTypes.MULTI_SELECT,
    options: applicationStatusRd,
  },

  {
    key: APPLICATION_COUNTRY_REGION,
    text: "Country/Region",
    type: fieldTypes.COUNTRY,
  },
  {
    key: APPLICATION_STATE_PROVINCE,
    text: "State/Province",
    type: fieldTypes.PROVINCE,
  },
];

export const mvpActivitiesEventsFilterFields: IFilterField[] = [
  {
    key: FilterValueFieldNames.ACTIVITY_OR_EVENT,
    text: "Type",
    type: fieldTypes.MULTI_SELECT,
    options: [
      { key: "Activity", text: "Activity" },
      { key: "Event", text: "Event" },
    ],
  },
  {
    key: FilterValueFieldNames.TECHNOLOGY_AREA_GROUP,
    text: "Award Category",
    type: fieldTypes.TECHNOLOGY_AREA_GROUP,
  },
  {
    key: FilterValueFieldNames.TECHNOLOGY_AREA,
    text: "Primary Technology Area",
    type: fieldTypes.TECHNOLOGY_AREA,
  },
  {
    key: FilterValueFieldNames.ACTIVITY_OR_EVENT_TYPE,
    text: "Activity/Event Type",
    type: fieldTypes.ACTIVITY_OR_EVENT_TYPE,
  },
];

export const defaultFilterValues: {
  fieldName: string;
  value: string;
}[] = [
  { fieldName: APPLICATION_TYPE, value: ApplicationTypeEnum.ANY },
  { fieldName: APPLICATION_STATUS, value: ApplicationStatusTypesEnum.REVIEW_ELIGIBLE },
  { fieldName: APPLICATION_STATUS, value: ApplicationStatusTypesEnum.UNDER_REVIEW },
  { fieldName: APPLICATION_STATUS, value: ApplicationStatusTypesEnum.Submitted },
];

/**
 * Default filter values for mvp highlighted and additional activities and events
 */
export const defaultActivitiesEventsFilterValues: {
  fieldName: string;
  value: string;
}[] = [];

/**
 * Adding this to show information regarding the assign application side panel functionality
 */
const SidePanelInformation =
  "Each Technology Area can be assigned to a Product Group Lead to review and score application. You can either assign it to yourself or another PGL in the same Award Category.";
const UpdateTechnologyAreaSidePanelInformation =
  'Resetting the score will roll it back to "Unscored".';
const NoAssigneesErrorMessage =
  "None of the Technology Areas on the applications you selected match with your Award Category";
export const SidePanelConstants = {
  SidePanelInformation: SidePanelInformation,
  UpdateTechnologyAreaSidePanelInformation: UpdateTechnologyAreaSidePanelInformation,
  NoAssigneesErrorMessage: NoAssigneesErrorMessage,
};

/**
 * Constant for the "Start Review" review status of an application.
 */
export const APPLICATION_REVIEW_STATUS_START_REVIEW = "Start Review";
export const APPLICATION_REVIEW_STATUS_NOT_READY = "Not Ready";
export const APPLICATION_REVIEW_STATUS_UNDER_REVIEW = "Under Review";
export const APPLICATION_REVIEW_STATUS_REVIEW_COMPLETED = "Completed";

export const ApplicationReviewStatuses = {
  APPLICATION_REVIEW_STATUS_START_REVIEW: APPLICATION_REVIEW_STATUS_START_REVIEW,
  APPLICATION_REVIEW_STATUS_NOT_READY: APPLICATION_REVIEW_STATUS_NOT_READY,
  APPLICATION_REVIEW_STATUS_UNDER_REVIEW: APPLICATION_REVIEW_STATUS_UNDER_REVIEW,
  APPLICATION_REVIEW_STATUS_REVIEW_COMPLETED: APPLICATION_REVIEW_STATUS_REVIEW_COMPLETED,
};

export const ReviewSubSiteUrlPath = Object.freeze({
  applications: "applications",
  demographics: "demographics",
});

//need this due to circular dependency between application/profile constants
const genders: IKeyText[] = [
  { key: "Woman", text: "Woman" },
  { key: "Man", text: "Man" },
  { key: "Non-Binary", text: "Non-Binary" },
  { key: "Choose not to answer", text: "Choose not to answer" },
];

/**
 * Sidebar filter constants used for rendering
 */
export const mvpApplicationDemographicsFilterFields: IFilterField[] = [
  {
    key: APPLICATION_TYPE,
    text: "Application Type",
    type: fieldTypes.MULTI_SELECT,
    options: applicationTypesWithoutAny,
  },
  {
    key: FilterValueFieldNames.COUNTRY,
    text: "Country",
    type: fieldTypes.COUNTRY,
  },
  {
    key: FilterValueFieldNames.REGION,
    text: "Region",
    type: fieldTypes.CL_REGION,
  },
  {
    key: FilterValueFieldNames.TECHNOLOGY_AREA_GROUP,
    text: "Award Category",
    type: fieldTypes.TECHNOLOGY_AREA_GROUP,
  },
  {
    key: FilterValueFieldNames.TECHNOLOGY_AREA,
    text: "Technology Area",
    type: fieldTypes.TECHNOLOGY_AREA,
  },
  {
    key: FilterValueFieldNames.GENDER,
    text: "Gender",
    type: fieldTypes.MULTI_SELECT,
    options: genders,
  },
];

/**
 * Default filter values for mvp demographics.
 */
export const defaultMvpDemographicsFilterValues: {
  fieldName: string;
  value: string;
}[] = [];

// Threshold for the MVP application URL length
export const maxURLLength = 2000;

// Error message for MVP application URL length
export const urlLengthError = "URL too long, please refine your search";

// Error message for MVP application URL when invalid parameters are passed
export const urlInvalidError = "Invalid URL parameters, resetting to default filters.";
